import React, {
  Fragment,
  useLayoutEffect,
  useState,
  useRef,
  useEffect,
  useContext,
} from "react";
import {
  Alert,
  AppBar,
  Badge,
  Box,
  Container,
  IconButton,
  Slide,
  SlideProps,
  Snackbar,
  Toolbar,
  Typography,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import { Pages } from "../../constants/data";
import CustomDrawer from "../drawer/CustomDrawer";
import Cart from "./Cart";
import {
  getCartItem,
  getFromLocalStorage,
  saveInLocalStorage,
} from "../../config/localStorage";
import AppContext from "../../config/appContext";
import CustomerInformation from "./CustomerInformation";
import PaymentInformation from "./PaymentInformation";
import OrderSummary from "./OrderSummary";
import OrderConfirmation from "./OrderConfirmation";
import { submitOrder } from "../../apis/order.api";
import { createShipment } from "../../apis/shipping.api";
import LoadingWithAnimation from "../loading/LoadingWithAnimation";
import RecipientInformation from "./RecipientInformation";

interface BusinessNavbarProps {
  businessInfo: Record<string, string | number | boolean | any>;
}

const componentThatCanDisplay = {
  1: "CART",
  2: "CUSTOMERINFORMATION",
  3: "PAYMENTINFORMATION",
  4: "ORDERSUMMARY",
};

const BusinessNavbar: React.FC<BusinessNavbarProps> = ({ businessInfo }) => {
  const {
    _id: companyId,
    name: companyName,
    slug,
    checkoutSystem,
  } = businessInfo;

  const navigate = useNavigate();

  const { cartUpdated } = useContext(AppContext);

  const theme = useTheme();

  const [currentPage, setCurrentPage] = useState(0);
  const [isCustomerInformationAdded, setIsCustomerInformationAdded] =
    useState<boolean>(false);
  const [cartItems, setCartItems] = useState([]); // Example cart items count
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shippingInfoCreated, setShippingInfoCreated] = useState<
    Record<string, any>
  >({});
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [orderDetails, setOrderDetails] = useState<Record<string, any>>({});
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [orderReceipt, setOrderReceipt] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [shippingError, setShippingError] = useState("");
  const [orderSubmitError, setOrderSubmitError] = useState("");

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("info");
  const [alertMessage, setAlertMessage] = useState("");

  const [paymentMethod, setPaymentMethod] = useState<
    Record<string, any | undefined>
  >({});
  const [isBillingSame, setIsBillingSame] = useState<boolean>(true);
  const [componentToDisplay, setComponentToDisplay] = useState(
    componentThatCanDisplay[1]
  );

  const navbarRef = useRef<HTMLDivElement>(null);

  //   update drawer title
  const drawerTitle = () => {
    if (componentToDisplay === "CART") {
      return `Your Bag ${
        cartItems?.length > 0 ? `(${cartItems?.length})` : ""
      }`;
    }
    return "";
  };

  // Get the current page
  useLayoutEffect(() => {
    const pathName = window.location.pathname;
    const filter = Pages.findIndex((page) => page.link === pathName);
    setCurrentPage(filter);
  }, []);

  useEffect(() => {
    if (navbarRef.current) {
      setNavbarHeight(navbarRef.current.clientHeight);
    }
  }, []);

  const handleClick = () => {
    if (drawerOpen === true) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  };

  const handleShippingInformation = async () => {
    setIsLoading(true);
    const customerInfo = getFromLocalStorage("customerInfo");
    const isCreated = createShipment(customerInfo, cartItems, companyId).then(
      (res) => {
        if (res?.error === true) {
          // console.log({ res });
          // setShippingError(
          //   "An Error Occurred When Getting Your Shipping Info, Please Try Again!"
          // );

          showAlert(
            "An Error Occurred When Getting Your Shipping Info, Please Try Again!.",
            "error"
          );
          setIsCustomerInformationAdded(false);
          return false;
        }
        // TODO: add the price to the order details
        setShippingError("");
        setShippingInfoCreated(res);
        console.log("Shipping Info Created: ", res);
        saveInLocalStorage("shippingInfo", res.data.rateSelected);
        saveInLocalStorage("shippingInfo", res?.data?.rateSelected);
        const discountDetails = res?.data?.discountDetails;
        if (discountDetails.shippingDiscount) {
          const tempCart = getCartItem(`cart-${slug}`);
          tempCart.discountDetails = discountDetails;
          saveInLocalStorage(`cart-${slug}`, tempCart);
          setOrderDetails({
            ...orderDetails,
            shippingFees: res?.data?.rateSelected?.amount,
            discount: discountDetails,
          });

          // setCartItems(tempCart?.items);
          // saveInLocalStorage(
          //   "finalShippingAmount",
          //   shippingAmount.finalShippingAmount
          // );
          // saveInLocalStorage("discount", shippingAmount.shippingDiscount);
        }
        return true;
      }
    );
    setIsLoading(false);
    return isCreated;
  };

  const handleSubmitOrder = async () => {
    setIsLoading(true);
    const tempCart = getCartItem(`cart-${slug}`);
    const customerInfo = getCartItem("customerInfo");
    console.log({ orderDetails });
    const finalDetail = {
      paymentMethod,
      cart: tempCart,
      customerInfo,
      totalSummary: {
        subTotal: orderDetails?.subTotal || 0,
        serviceFees: orderDetails?.servicesFees || 0,
        taxes: orderDetails?.taxes || 0,
        shippingFees: orderDetails?.shippingFees || 0,
        delivery: orderDetails?.delivery || 0,
        pickup: orderDetails?.pickupsFees || 0,
        discount: orderDetails?.discount || 0,
        total: orderDetails?.total || 0,
      },
      shippingInfo: shippingInfoCreated.data || {},
      recipientInfo: orderDetails.recipientInfo,
    };
    await submitOrder(finalDetail).then((res) => {
      console.log({ res });
      if (res?.error === true) {
        console.log({ res });
        setOrderSubmitError(
          "An Error Occurred When Submitting Your Order, Please Try Again!"
        );
        setOrderConfirmed(false);
        showAlert(
          "An Error Occurred When Submitting Your Order, Please Try Again!",
          "error"
        );
        return false;
      } else {
        setOrderSubmitError("");
        setOrderConfirmed(true);
        setOrderReceipt(res?.receiptNumber);
        showAlert("Order submitted successfully!", "success");

        setTimeout(() => {
          // localStorage.clear();
          window.location.reload();
        }, 24 * 60 * 60 * 1000);
      }
    });
    setIsLoading(false);
  };

  const closeConfirmationTab = () => {
    // localStorage.clear();
    window.location.reload();
  };

  const showAlert = (
    message: string,
    severity: "success" | "error" | "warning" | "info"
  ) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const SlideTransition = (props: SlideProps) => {
    return <Slide {...props} direction="left" />;
  };

  const componentRenderer = (component: string) => {
    switch (component) {
      case "CART":
        return (
          <Cart
            businessInfo={businessInfo}
            setComponentToDisplay={setComponentToDisplay}
          />
        );
      case "CUSTOMERINFORMATION":
        return (
          <CustomerInformation
            businessInfo={businessInfo}
            setIsCustomerInformationAdded={setIsCustomerInformationAdded}
            setComponentToDisplay={setComponentToDisplay}
            isLoading={isLoading}
            shippingError={shippingError}
          />
        );
      case "RECIPIENTINFORMATION":
        return (
          <RecipientInformation setComponentToDisplay={setComponentToDisplay} />
        );
      case "PAYMENTINFORMATION":
        return (
          <PaymentInformation
            businessInfo={businessInfo}
            setComponentToDisplay={setComponentToDisplay}
            setPaymentMethod={setPaymentMethod}
            isBillingSame={isBillingSame}
            setIsBillingSame={setIsBillingSame}
          />
        );
      case "ORDERSUMMARY":
        return (
          <OrderSummary
            businessInfo={businessInfo}
            setComponentToDisplay={setComponentToDisplay}
            setOrderDetails={setOrderDetails}
            handleSubmitOrder={handleSubmitOrder}
            paymentMethod={paymentMethod}
            isLoading={isLoading}
            orderSubmitError={orderSubmitError}
          />
        );

      default:
        break;
    }
  };

  // Customer information component change
  const handleCustomerInformationChange = async () => {
    setIsLoading(true);
    const tempCart = getCartItem(`cart-${slug}`);

    if (isCustomerInformationAdded) {
      if (checkoutSystem === "PREPAY") {
        let isShippingInfoCreated;
        if (tempCart.receptionMethod === "shipping") {
          isShippingInfoCreated = await handleShippingInformation();
        } else {
          isShippingInfoCreated = true;
        }

        if (isShippingInfoCreated && shippingError === "") {
          setComponentToDisplay("PAYMENTINFORMATION");
        }
      } else if (
        checkoutSystem === "POSTPAY" &&
        (tempCart.receptionMethod === "collection" ||
          tempCart.receptionMethod === "dropOff")
      ) {
        setComponentToDisplay("RECIPIENTINFORMATION");
      } else {
        setComponentToDisplay("ORDERSUMMARY");
      }
    }
    setIsLoading(false);
    setIsCustomerInformationAdded(false);
  };

  useEffect(() => {
    const tempCart = getCartItem(`cart-${slug}`);
    if (tempCart) setCartItems(tempCart?.items);
  }, [cartUpdated, slug]);

  useEffect(() => {
    handleCustomerInformationChange();
  }, [checkoutSystem, isCustomerInformationAdded, slug, shippingError]);

  // console.log({ orderDetails });

  if (isLoading) {
    return <LoadingWithAnimation open={isLoading} transparent={true} />;
  }

  return (
    <Fragment>
      <AppBar
        position="fixed"
        sx={{ backgroundColor: theme.palette.primary.light }}
        ref={navbarRef}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Typography
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                color: theme.palette.primary.main,
                cursor: "pointer",
                letterSpacing: "3px",
                fontSize: "24px",
                fontWeight: "bold",
                fontFamily: theme.typography.fontFamily,
              }}
              onClick={() => {
                window.scrollTo(0, 0);
                navigate("/");
              }}
            >
              {companyName}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                size="large"
                aria-label={`show ${cartItems.length} new items`}
                // color={""}
                onClick={handleClick}
              >
                {cartItems.length > 0 ? (
                  <Badge badgeContent={cartItems.length} color="error">
                    <ShoppingCartIcon />
                  </Badge>
                ) : (
                  <ShoppingCartIcon />
                )}
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Toolbar />
      <CustomDrawer
        drawerTitle={drawerTitle()}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {componentRenderer(componentToDisplay)}
      </CustomDrawer>
      {orderConfirmed && (
        <OrderConfirmation
          open={orderConfirmed}
          onClose={closeConfirmationTab}
          orderNumber={orderReceipt}
          orderDetails={orderDetails}
          paymentMethod={paymentMethod}
          businessInfo={businessInfo}
        />
      )}
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

export default BusinessNavbar;
